import type { RootStore } from 'logic';
import { comparer, reaction, runInAction } from 'mobx';
import { FieldValue, db as firestoreDb, BATCH_MAX } from 'firebase-modules';
import {
  CardStatus,
  safeBatch,
  CardDateEntry,
  CardDateType,
  CardPriority,
  CardIssue,
  FirestoreCard,
  StepHistoryEvent,
} from '@creative-kit/shared';
import { getVisitCardsLabel } from 'ui/moduleUxUtils';
import { Card } from './card';
import { RouteNames } from './routes';
import { QueueTabName, STEP_TAB_TRIAGE } from './ui-active-session';
import type { BreakoutPosition } from './ui-breakout';
import { db } from './data';
import { emitCardEvent } from './card-events';
import { buildDateChangeEventsForCard, buildDatesChangedMeta } from './cardEventUtils';
import {
  getRestoreDestinationDisplayLabel,
  getRestoreCardMutation,
  insertCardIdsAtBoundary,
  RESTORE_DESTINATION_ARCHIVE,
  RESTORE_DESTINATION_UNASSIGNED,
} from './sessionRestoreUtils';
import type { RestoreDestination } from './sessionRestoreUtils';

export const NEW_SOURCE_TAB = 'NEW_SOURCE_TAB';
export const COLLAPSED_ROW_ESTIMATED_SIZE_PX = 122;

export class SessionCardActionsStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  getCardById = (id: string): Card | undefined => this.rootStore.cardsStore.cards[id];

  private createVisitAssetFunction = (cardIds: string[]) => {
    return () => {
      if (cardIds.length === 0) return;

      // Focus on the first card (or the top one if multiple)
      const cardId = cardIds[0];
      this.rootStore.uiCardView.toggleCardFocus(cardId);
    };
  };

  get currentSession() {
    return this.rootStore.sessionsStore.currentSession!;
  }

  private completeCardOnly = (cardIds: string[]) => {
    const session = this.rootStore.sessionsStore.currentSession;
    if (!session) return async () => {};

    const uid = this.rootStore.authStore.user?.uid ?? '';
    const email = this.rootStore.authStore.user?.email ?? null;

    safeBatch(firestoreDb, cardIds, (batch, cardId) => {
      const card = this.getCardById(cardId);
      if (card) {
        card.createCommentIfNeeded();
        batch.set(
          session.cardListRef.doc(cardId),
          {
            doneAt: FieldValue.serverTimestamp() as unknown as Date,
            filename: card.filename,
          },
          {
            merge: true,
          },
        );
      }
    });

    cardIds.forEach((cardId) => {
      emitCardEvent(session.id, cardId, 'card.completed', uid, email);
    });

    // if not the current card, we need to move its place in the cardId list
    const cardIdsCopy = [...session.data.cardIds].filter((id) => !cardIds.includes(id));
    const currentCardIdx = session.cardList.findIndex((c) => c.status === CardStatus.Current);
    if (currentCardIdx >= 0) {
      cardIdsCopy.splice(currentCardIdx, 0, ...cardIds);
      session.sessionRef.set(
        {
          cardIds: cardIdsCopy,
        },
        {
          merge: true,
        },
      );
    }

    // If in card view, return user to queue view after marking card as done
    if (this.rootStore.uiCardView.isCardFocused) {
      this.rootStore.routerStore.goTo(RouteNames.SESSION_QUEUE, {
        params: { sessionId: this.rootStore.sessionsStore.currentSessionId },
      });
    }

    return async () => {
      for (const cardId of cardIds) {
        session.cardListRef.doc(cardId).update({
          doneAt: FieldValue.delete(),
          filename: FieldValue.delete(),
        });
      }
    };
  };

  private async batchRemoveCards(cardIds: string[]) {
    const deletedCardIds = [...cardIds];
    const deletedCards = cardIds.map((id) => this.getCardById(id));
    const originalCardData = deletedCards.map((card) => ({ id: card?.id, data: card?.data }));
    const originalCardIds = [...this.currentSession.data.cardIds];

    try {
      this.rootStore.uiActiveSessionStore.setBatchDeleteLoading(true);

      // Use transaction to atomically delete cards and update cardIds array
      const userId = this.rootStore.authStore.user?.uid ?? '';
      const userEmail = this.rootStore.authStore.user?.email ?? null;
      await firestoreDb.runTransaction(async (transaction) => {
        // Mark cards as deleted
        deletedCardIds.forEach((cardId) => {
          const cardRef = this.currentSession.cardListRef.doc(cardId);
          transaction.set(
            cardRef,
            {
              deletedAt: FieldValue.serverTimestamp() as unknown as Date,
              deletedBy: {
                userId,
                email: userEmail,
              } as { email: string | null; userId: string },
            },
            { merge: true },
          );
        });

        // Remove from session's cardIds array in the same transaction
        // Also deduplicate the array to fix data corruption
        const deduplicatedCardIds = [...new Set(this.currentSession.data.cardIds)];
        const newCardIds = deduplicatedCardIds.filter((id) => !deletedCardIds.includes(id));

        transaction.set(
          this.currentSession.sessionRef,
          {
            cardIds: newCardIds,
          },
          { merge: true },
        );
      });

      deletedCardIds.forEach((cardId) => {
        emitCardEvent(this.currentSession.id, cardId, 'card.deleted', userId, userEmail);
      });
    } catch (error) {
      console.error('batchRemoveCards transaction failed:', error);
      throw error;
    } finally {
      this.rootStore.uiActiveSessionStore.setBatchDeleteLoading(false);
    }

    return async () => {
      // Restore cards: remove deleted fields and restore to original cardIds
      await firestoreDb.runTransaction(async (transaction) => {
        // Restore card data
        originalCardData.forEach((cardData) => {
          if (cardData?.id && cardData?.data) {
            const cardRef = this.currentSession.cardListRef.doc(cardData.id);
            transaction.set(cardRef, cardData.data);
          }
        });

        // Restore original cardIds array
        transaction.set(
          this.currentSession.sessionRef,
          {
            cardIds: originalCardIds,
          },
          { merge: true },
        );
      });
    };
  }

  completeCard = async (cardId: string) => {
    const card = this.rootStore.cardsStore.cards[cardId];
    const undo = await this.completeCardOnly([cardId]);
    const visitAssetFn = this.createVisitAssetFunction([cardId]);

    this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
      undo,
      `Card #${card.cardNumber} marked as done`,
      visitAssetFn,
      'Visit Card',
    );

    this.rootStore.uiCardView.exitCardFocus();
  };

  private markCardInProgressOnly = async (cardIds: string[]) => {
    const originalCardIds = [...this.currentSession.data.cardIds];
    const originalDoneAtById: Record<string, Date | undefined> = {};
    const reopenedIds: string[] = [];
    safeBatch(firestoreDb, cardIds, (batch, cardId) => {
      const card = this.rootStore.cardsStore.cards[cardId];
      if (card.status === CardStatus.Completed) {
        originalDoneAtById[cardId] = card.data.doneAt;
        reopenedIds.push(cardId);

        const cardRef = this.currentSession.cardListRef.doc(cardId);
        batch.set(
          cardRef,
          {
            doneAt: FieldValue.delete() as unknown as Date,
          },
          { merge: true },
        );
      }
    });

    const uid = this.rootStore.authStore.user?.uid ?? '';
    const email = this.rootStore.authStore.user?.email ?? null;
    reopenedIds.forEach((cardId) => {
      emitCardEvent(this.currentSession.id, cardId, 'card.reopened', uid, email);
    });

    const newCardIds = originalCardIds.filter((c) => !cardIds.includes(c)).concat(...cardIds);
    this.currentSession.sessionRef.set(
      {
        cardIds: newCardIds,
      },
      { merge: true },
    );

    return async () => {
      for (const cardId of cardIds) {
        this.currentSession.cardListRef.doc(cardId).set(
          {
            doneAt: originalDoneAtById[cardId],
          },
          { merge: true },
        );
      }
      this.currentSession.sessionRef.set(
        {
          cardIds: originalCardIds,
        },
        { merge: true },
      );
    };
  };

  private getNextStepCompleteTarget(
    cardId: string,
  ): { type: 'step'; stepId: string } | { type: 'done' } {
    const steps = this.currentSession.sortedSteps;
    const currentStepId = this.rootStore.cardsStore.cards[cardId]?.data.stepId ?? null;

    if (steps.length === 0) {
      return { type: 'done' };
    }

    if (currentStepId === null) {
      return { type: 'step', stepId: steps[0].id };
    }

    const currentStepIndex = steps.findIndex((step) => step.id === currentStepId);
    if (currentStepIndex === -1 || currentStepIndex === steps.length - 1) {
      return { type: 'done' };
    }

    return { type: 'step', stepId: steps[currentStepIndex + 1].id };
  }

  markCardInProgress = async (cardId: string) => {
    const card = this.rootStore.cardsStore.cards[cardId];
    const undo = await this.markCardInProgressOnly([cardId]);

    if (undo !== undefined) {
      const visitAssetFn = this.createVisitAssetFunction([cardId]);
      this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
        undo,
        `Card #${card.cardNumber} marked as Pending`,
        visitAssetFn,
        'Visit Card',
      );
    }
  };

  advanceCardsToNextStep = async (cardIds: string[]) => {
    const session = this.rootStore.sessionsStore.currentSession;
    if (!session) return false;

    const actionableCardIds = [...new Set(cardIds)].filter((cardId) => {
      const card = this.getCardById(cardId);
      return card && !card.data.deletedAt && card.status !== CardStatus.Completed;
    });

    if (actionableCardIds.length === 0) return false;

    const previousStepIds: Record<string, string | null> = {};
    const cardsToMove: Array<{ cardId: string; stepId: string }> = [];
    const cardIdsToComplete: string[] = [];

    actionableCardIds.forEach((cardId) => {
      previousStepIds[cardId] = this.getCardById(cardId)?.data.stepId ?? null;
      const target = this.getNextStepCompleteTarget(cardId);
      if (target.type === 'done') {
        cardIdsToComplete.push(cardId);
        return;
      }

      cardsToMove.push({ cardId, stepId: target.stepId });
    });

    const movedCardIds: string[] = [];
    let undoComplete: (() => Promise<void>) | undefined;

    try {
      for (const { cardId, stepId } of [...cardsToMove].reverse()) {
        // eslint-disable-next-line no-await-in-loop
        await session.moveCardToStep(cardId, stepId, true);
        movedCardIds.push(cardId);
      }

      if (cardIdsToComplete.length > 0) {
        undoComplete = await this.completeCardOnly(cardIdsToComplete);
      }
    } catch (error) {
      for (const cardId of movedCardIds) {
        // eslint-disable-next-line no-await-in-loop
        await session.moveCardToStep(cardId, previousStepIds[cardId] ?? null, true);
      }

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Unable to complete step. Please try again.',
      });
      return false;
    }

    const { deselectAllCards } = this.rootStore.uiActiveSessionStore;
    deselectAllCards();

    const undo = async () => {
      if (undoComplete) {
        await undoComplete();
      }

      for (const cardId of movedCardIds) {
        // eslint-disable-next-line no-await-in-loop
        await session.moveCardToStep(cardId, previousStepIds[cardId] ?? null, true);
      }
    };

    const visitAssetFn = this.createVisitAssetFunction(actionableCardIds);
    const description =
      actionableCardIds.length === 1
        ? 'Step completed'
        : `${actionableCardIds.length} cards step completed`;

    this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
      undo,
      description,
      visitAssetFn,
      getVisitCardsLabel(actionableCardIds.length),
    );

    return true;
  };

  toggleCardProgress = (cardId: string) => {
    const card = this.rootStore.cardsStore.cards[cardId];
    if (card) {
      if (card.status === CardStatus.Completed) {
        this.markCardInProgress(cardId);
      } else {
        this.completeCard(cardId);
      }
    }
  };

  promoteCardAndCompleteCurrent = (cardId: string) => {
    const {
      currentCardIdx,
      currentCardId,
      data: { cardIds },
    } = this.currentSession;
    this.completeCardOnly([currentCardId]);
    const promotedCardIdx = cardIds.indexOf(cardId);
    const newCurrentCardIdx = currentCardIdx + 1;
    cardIds.splice(newCurrentCardIdx, 0, cardIds.splice(promotedCardIdx, 1)[0]);
    this.currentSession.sessionRef.set(
      {
        cardIds,
      },
      { merge: true },
    );
  };

  promoteCardAndQueueCurrent = (cardId: string) => {
    const { currentCardIdx } = this.currentSession;
    this.moveCardToIndex(cardId, currentCardIdx);
  };

  moveCardToIndex = (cardId: string, indexToMove: number) => {
    const cardIds = [...this.currentSession.data.cardIds];
    const promotedCardIdx = cardIds.indexOf(cardId);
    cardIds.splice(indexToMove, 0, cardIds.splice(promotedCardIdx, 1)[0]);
    return this.currentSession.sessionRef.set(
      {
        cardIds,
      },
      { merge: true },
    );
  };

  promoteCardToUpNext = (cardId: string) => {
    this.moveCardToIndex(cardId, this.currentSession.upNextCardIdx);
  };

  promoteCardToOnDeck = (cardId: string) => {
    this.moveCardToIndex(cardId, this.currentSession.onDeckCardIdx);
  };

  moveCardToLast = (cardId: string) => {
    this.moveCardToIndex(cardId, this.currentSession.data.cardIds.length - 1);
  };

  removeCard = async (cardId: string) => {
    const card = this.rootStore.cardsStore.cards[cardId];
    const queueName = this.currentSession.inProgressCardIds.includes(cardId) ? 'Pending' : 'Done';
    const undo = await this.batchRemoveCards([cardId]);
    this.rootStore.uiSnackbarStore.setUndoDetails(
      undo,
      `Card #${card.cardNumber} deleted from ${queueName}`,
    );

    // If in card view, return user to queue view after deleting card
    if (card.isFocused) {
      this.rootStore.routerStore.goTo(RouteNames.SESSION_QUEUE, {
        params: { sessionId: this.rootStore.sessionsStore.currentSessionId },
      });
    }
  };

  batchRemoveVisibleCards = async () => {
    const visibleCards = this.currentSession.getVisible(this.currentSession.inProgressCardIds);
    const deletedCards = [...visibleCards];
    visibleCards.forEach((c) => c.setModificationState('deleting'));
    const undo = await this.batchRemoveCards(deletedCards.map(({ id }) => id));
    visibleCards.forEach((c) => c.resetMotificationState());
    this.rootStore.uiSnackbarStore.setUndoDetails(
      undo,
      `${deletedCards.length} Cards Removed from Queue`,
    );
  };

  removeSelectedCards = async () => {
    const { selectedCardIdsInCurrentStep, selectedCards, deselectAllCards } =
      this.rootStore.uiActiveSessionStore;
    const cardIds = [...selectedCardIdsInCurrentStep];
    if (cardIds.length === 0) return;

    selectedCards.forEach((c) => c.setModificationState('deleting'));
    const undo = await this.batchRemoveCards(cardIds);
    deselectAllCards();
    selectedCards.forEach((c) => c.resetMotificationState());
    this.rootStore.uiSnackbarStore.setUndoDetails(undo, `${cardIds.length} cards removed`);
  };

  /** Soft-delete cards (e.g. drag onto Trash step tab). */
  softDeleteCards = async (cardIds: string[]) => {
    const ids = [...new Set(cardIds.filter(Boolean))];
    if (ids.length === 0) return;

    const cards = ids.map((id) => this.rootStore.cardsStore.cards[id]).filter(Boolean);
    cards.forEach((c) => c.setModificationState('deleting'));
    const undo = await this.batchRemoveCards(ids);
    this.rootStore.uiActiveSessionStore.deselectAllCards();
    cards.forEach((c) => c.resetMotificationState());
    this.rootStore.uiSnackbarStore.setUndoDetails(undo, `${ids.length} cards removed`);
  };

  duplicateCard = async (cardId: string, duplicateIdx?: number) => {
    const cardToCopy = this.rootStore.cardsStore.cards[cardId];

    const { undo, duplicatedCardIds } = await this.duplicateCardOnly([cardId], duplicateIdx);
    this.rootStore.uiSnackbarStore.setUndoDetails(
      undo,
      `Card #${cardToCopy.cardNumber} duplicated below original card`,
    );

    // Add glow animation for single card duplication (don't add to New Cards tab)
    duplicatedCardIds.forEach((duplicatedCardId) => {
      this.rootStore.uiActiveSessionStore.addRecentlyCreatedCard(duplicatedCardId);
    });

    // When focusing a card, redirect to newly duplicated card
    if (this.rootStore.uiCardView.isCardFocused && duplicatedCardIds.length > 0) {
      this.rootStore.uiCardView.toggleCardFocus(duplicatedCardIds[0]);
    }
  };

  duplicateSelectedCards = async () => {
    const { selectedCardIds, deselectAllCards } = this.rootStore.uiActiveSessionStore;
    const cardIds = [...selectedCardIds];

    const duplicatePosition = this.currentSession.data.cardIds.length;

    const { undo, duplicatedCardIds } = await this.duplicateCardOnly(cardIds, duplicatePosition);

    this.rootStore.uiSnackbarStore.setUndoDetails(
      undo,
      `${cardIds.length} cards duplicated at end of queue`,
    );
    deselectAllCards();

    // Add glow animation for duplicated cards
    setTimeout(() => {
      runInAction(() => {
        duplicatedCardIds.forEach((cardId) => {
          this.rootStore.uiActiveSessionStore.addRecentlyCreatedCard(cardId);
        });
      });
    }, 100);
  };

  reorderCards(reorderedCardId: string, targetCardId: string, reorderPosition: 'before' | 'after') {
    const beforeCardIds = [...this.currentSession.data.cardIds];
    const finalCardIds = [...this.currentSession.data.cardIds];
    const reorderedCardIdx = finalCardIds.indexOf(reorderedCardId);
    finalCardIds.splice(reorderedCardIdx, 1);
    const targetCardIdx = finalCardIds.indexOf(targetCardId);
    const reorderedCard = this.rootStore.cardsStore.cards[reorderedCardId];

    finalCardIds.splice(
      reorderPosition === 'before' ? targetCardIdx : targetCardIdx + 1,
      0,
      reorderedCardId,
    );

    this.currentSession.sessionRef.set(
      {
        cardIds: finalCardIds,
      },
      { merge: true },
    );

    let queueChanged = false;
    const queueChangeUnsubscribe = reaction(
      () => this.currentSession.data.cardIds,
      (newCardIds) => {
        // Card id reordering has changed
        if (!comparer.shallow(newCardIds, finalCardIds)) {
          queueChanged = true;
          queueChangeUnsubscribe();
          revokeUndo();
        }
      },
      { equals: comparer.shallow },
    );
    const revokeUndo = this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      queueChangeUnsubscribe();
      if (queueChanged) {
        return;
      }
      await this.currentSession.sessionRef.set(
        {
          cardIds: beforeCardIds,
        },
        { merge: true },
      );
    }, `Card #${reorderedCard.cardNumber} reordered`);
  }

  reorderMultipleCards(
    reorderedCardIds: string[],
    targetCardId: string,
    reorderPosition: 'before' | 'after',
  ) {
    if (reorderedCardIds.length === 0) {
      return;
    }

    // If only one card, use the existing single-card reorder logic
    if (reorderedCardIds.length === 1) {
      this.reorderCards(reorderedCardIds[0], targetCardId, reorderPosition);
      return;
    }

    const beforeCardIds = [...this.currentSession.data.cardIds];
    const finalCardIds = [...this.currentSession.data.cardIds];

    // Get the indices of all cards to be reordered, sorted by their position in the queue (top to bottom)
    const reorderedIndices = reorderedCardIds
      .map((id) => finalCardIds.indexOf(id))
      .filter((idx) => idx !== -1)
      .sort((a, b) => a - b);

    // Remove all reordered cards from their current positions (in reverse order to maintain indices)
    const cardsToMove: string[] = [];
    for (let i = reorderedIndices.length - 1; i >= 0; i--) {
      const idx = reorderedIndices[i];
      cardsToMove.unshift(finalCardIds.splice(idx, 1)[0]);
    }

    // Find the target position
    const targetCardIdx = finalCardIds.indexOf(targetCardId);
    const insertIdx = reorderPosition === 'before' ? targetCardIdx : targetCardIdx + 1;

    // Insert all cards at the target position, maintaining their queue order
    finalCardIds.splice(insertIdx, 0, ...cardsToMove);

    this.currentSession.sessionRef.set(
      {
        cardIds: finalCardIds,
      },
      { merge: true },
    );

    let queueChanged = false;
    const queueChangeUnsubscribe = reaction(
      () => this.currentSession.data.cardIds,
      (newCardIds) => {
        // Card id reordering has changed
        if (!comparer.shallow(newCardIds, finalCardIds)) {
          queueChanged = true;
          queueChangeUnsubscribe();
          revokeUndo();
        }
      },
      { equals: comparer.shallow },
    );
    const revokeUndo = this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      queueChangeUnsubscribe();
      if (queueChanged) {
        return;
      }
      await this.currentSession.sessionRef.set(
        {
          cardIds: beforeCardIds,
        },
        { merge: true },
      );
    }, `${reorderedCardIds.length} Assets Reordered`);
  }

  private duplicateCardOnly = async (cardIds: string[], duplicateIdx?: number) => {
    const beforeCardIds = [...this.currentSession.data.cardIds];
    const newCardIds = [...this.currentSession.data.cardIds];
    const duplicatedCardIds: string[] = [];
    const sourceToNewIds = new Map<string, string[]>();
    const uid = this.rootStore.authStore.user?.uid ?? '';
    const email = this.rootStore.authStore.user?.email ?? null;

    try {
      this.rootStore.uiActiveSessionStore.setDuplicationOperationLoading(true);

      for (const cardId of cardIds) {
        const cardRef = this.currentSession.cardListRef.doc();
        duplicatedCardIds.push(cardRef.id);
        const existing = sourceToNewIds.get(cardId) ?? [];
        existing.push(cardRef.id);
        sourceToNewIds.set(cardId, existing);

        const cardIdx = duplicateIdx ?? newCardIds.indexOf(cardId);
        const cardToCopy = this.rootStore.cardsStore.cards[cardId];
        const isSourceCardCompleted = cardToCopy.status === CardStatus.Completed;
        const data = { ...cardToCopy.data };
        if (isSourceCardCompleted) {
          delete data.doneAt;
          newCardIds.push(cardRef.id);
        } else {
          newCardIds.splice(cardIdx + 1, 0, cardRef.id);
        }

        data.duplicatedFromCardId = cardId;
        delete data.duplicatedChildCardIds;
        delete data.breakoutParentCardId;
        delete data.breakoutChildCardIds;
        delete data.breakoutModuleId;

        // eslint-disable-next-line no-await-in-loop
        await cardRef.set(data);

        emitCardEvent(this.currentSession.id, cardRef.id, 'card.created', uid, email, {
          via: 'duplication',
          sourceCardId: cardId,
        });
      }

      await this.currentSession.sessionRef.set(
        {
          cardIds: newCardIds,
        },
        { merge: true },
      );

      const sourceCardIds = [...sourceToNewIds.keys()];
      await safeBatch(firestoreDb, sourceCardIds, (batch, srcCardId) => {
        const newIds = sourceToNewIds.get(srcCardId) ?? [];
        batch.set(
          this.currentSession.cardListRef.doc(srcCardId),
          {
            duplicatedChildCardIds: FieldValue.arrayUnion(...newIds) as unknown as string[],
          },
          { merge: true },
        );
      });

      return {
        duplicatedCardIds,
        undo: async () => {
          await this.currentSession.sessionRef.set(
            {
              cardIds: beforeCardIds,
            },
            { merge: true },
          );
          for (const dupId of duplicatedCardIds) {
            emitCardEvent(this.currentSession.id, dupId, 'card.deleted', uid, email);
            // eslint-disable-next-line no-await-in-loop
            await this.currentSession.cardListRef.doc(dupId).delete();
          }
          await safeBatch(firestoreDb, sourceCardIds, (batch, srcCardId) => {
            const newIds = sourceToNewIds.get(srcCardId) ?? [];
            batch.set(
              this.currentSession.cardListRef.doc(srcCardId),
              {
                duplicatedChildCardIds: FieldValue.arrayRemove(...newIds) as unknown as string[],
              },
              { merge: true },
            );
          });
        },
      };
    } finally {
      this.rootStore.uiActiveSessionStore.setDuplicationOperationLoading(false);
    }
  };

  /** Soft-delete breakout artifacts (same semantics as batchRemoveCards, without altering session.cardIds). */
  private softDeleteCardDocuments = async (cardIds: string[]) => {
    if (cardIds.length === 0) return;

    const userId = this.rootStore.authStore.user?.uid ?? '';
    const userEmail = this.rootStore.authStore.user?.email ?? null;

    await safeBatch(firestoreDb, cardIds, (batch, cardId) => {
      batch.set(
        this.currentSession.cardListRef.doc(cardId),
        {
          deletedAt: FieldValue.serverTimestamp() as unknown as Date,
          deletedBy: {
            userId,
            email: userEmail,
          } as { email: string | null; userId: string },
        },
        { merge: true },
      );
    });

    cardIds.forEach((cardId) => {
      emitCardEvent(this.currentSession.id, cardId, 'card.deleted', userId, userEmail);
    });
  };

  private restoreBreakoutState = async ({
    originalCardIds,
    sourceCardSnapshots,
  }: {
    originalCardIds: string[];
    sourceCardSnapshots: Record<
      string,
      {
        breakoutChildCardIds?: string[];
        breakoutModuleId?: string;
        deletedAt?: Date;
        deletedBy?: {
          email: string | null;
          userId: string;
        };
      }
    >;
  }) => {
    // Phase A: restore cardIds array (single write, always ≤1 op)
    await this.currentSession.sessionRef.set({ cardIds: originalCardIds }, { merge: true });

    // Phase B: restore source card fields in chunked batches (bounded by BATCH_MAX via safeBatch)
    const cardEntries = Object.entries(sourceCardSnapshots);
    if (cardEntries.length > 0) {
      await safeBatch(firestoreDb, cardEntries, (batch, [cardId, snapshot]) => {
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        batch.update(cardRef, {
          breakoutChildCardIds: snapshot.breakoutChildCardIds ?? FieldValue.delete(),
          breakoutModuleId: snapshot.breakoutModuleId ?? FieldValue.delete(),
          deletedAt: snapshot.deletedAt ?? FieldValue.delete(),
          deletedBy: snapshot.deletedBy ?? FieldValue.delete(),
        });
      });
    }
  };

  breakoutCards = async (config: {
    sourceCardIds: string[];
    moduleId: string;
    keepOriginals: boolean;
    targetStepId: string | null;
    position: BreakoutPosition;
    onProgress?: (created: number, total: number) => void;
    isCancelled?: () => boolean;
  }) => {
    if (!this.rootStore.authStore.user) return;

    const sourceCardIdSet = new Set(config.sourceCardIds);
    const sourceCardIds = this.currentSession.data.cardIds.filter((id) => sourceCardIdSet.has(id));
    const sourceCards = sourceCardIds
      .map((id) => this.rootStore.cardsStore.cards[id])
      .filter((card): card is Card => !!card);

    if (sourceCards.length === 0) return;

    const targetModule = this.currentSession.data.modules[config.moduleId];
    if (!targetModule) return;

    const originalCardIds = [...this.currentSession.data.cardIds];
    const user = this.rootStore.authStore.user;
    const sourceCardSnapshots = sourceCards.reduce(
      (byId, card) => ({
        ...byId,
        [card.id]: {
          breakoutChildCardIds: card.data.breakoutChildCardIds
            ? [...card.data.breakoutChildCardIds]
            : undefined,
          breakoutModuleId: card.data.breakoutModuleId,
          deletedAt: card.data.deletedAt,
          deletedBy: card.data.deletedBy,
        },
      }),
      {} as Record<
        string,
        {
          breakoutChildCardIds?: string[];
          breakoutModuleId?: string;
          deletedAt?: Date;
          deletedBy?: {
            email: string | null;
            userId: string;
          };
        }
      >,
    );

    const newCardIdsByParent: Record<string, string[]> = {};
    const breakoutCardsData: Array<{ cardId: string; parentCardId: string; data: FirestoreCard }> =
      [];
    for (const sourceCard of sourceCards) {
      const rowIds = sourceCard.getConnectedRows(config.moduleId);
      if (rowIds.length === 0) continue;

      newCardIdsByParent[sourceCard.id] = [];

      for (const rowId of rowIds) {
        const newCardId = this.currentSession.cardListRef.doc().id;
        newCardIdsByParent[sourceCard.id].push(newCardId);

        const baseData = sourceCard.data;
        const breakoutData: FirestoreCard = {
          connections: {
            ...baseData.connections,
            [config.moduleId]: targetModule.multi ? [rowId] : rowId,
          },
          createdAt: FieldValue.serverTimestamp() as unknown as Date,
          createdBy: {
            userId: user.uid,
            email: user.email ?? null,
          },
          ...(baseData.suffix ? { suffix: baseData.suffix } : {}),
          ...(baseData.manualTitle ? { manualTitle: baseData.manualTitle } : {}),
          ...(baseData.dates ? { dates: baseData.dates } : {}),
          ...(baseData.priority ? { priority: baseData.priority } : {}),
          ...(baseData.issue ? { issue: baseData.issue } : {}),
          ...(config.targetStepId
            ? {
                stepId: config.targetStepId,
                stepEnteredAt: FieldValue.serverTimestamp() as unknown as Date,
              }
            : {}),
          breakoutParentCardId: sourceCard.id,
        };

        breakoutCardsData.push({
          cardId: newCardId,
          parentCardId: sourceCard.id,
          data: breakoutData,
        });
      }
    }

    const newCardIds = breakoutCardsData.map((item) => item.cardId);
    if (newCardIds.length === 0) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'No breakout connections found for selected cards.',
      });
      return;
    }

    let finalCardIds = [...originalCardIds];
    if (config.position === 'after-source') {
      const cardIdsWithBreakouts: string[] = [];
      for (const cardId of originalCardIds) {
        cardIdsWithBreakouts.push(cardId);
        if (newCardIdsByParent[cardId]) {
          cardIdsWithBreakouts.push(...newCardIdsByParent[cardId]);
        }
      }
      finalCardIds = cardIdsWithBreakouts;
    } else {
      const targetStepCardIds = this.currentSession.getCardIdsByStepId(config.targetStepId);
      if (targetStepCardIds.length === 0) {
        finalCardIds =
          config.position === 'top'
            ? [...newCardIds, ...finalCardIds]
            : [...finalCardIds, ...newCardIds];
      } else {
        const firstTargetIdx = finalCardIds.indexOf(targetStepCardIds[0]);
        const lastTargetIdx = finalCardIds.indexOf(targetStepCardIds[targetStepCardIds.length - 1]);
        const insertIdx =
          config.position === 'top'
            ? Math.max(0, firstTargetIdx)
            : Math.min(finalCardIds.length, lastTargetIdx + 1);
        finalCardIds = [
          ...finalCardIds.slice(0, insertIdx),
          ...newCardIds,
          ...finalCardIds.slice(insertIdx),
        ];
      }
    }

    if (!config.keepOriginals) {
      finalCardIds = finalCardIds.filter((id) => !sourceCardIdSet.has(id));
    }

    const totalCardsToCreate = breakoutCardsData.length;
    let createdCardIds: string[] = [];

    try {
      const pendingCards = [...breakoutCardsData];
      // When adding step history, each breakout card consumes two batch writes:
      // one for the card doc and one for the append-only step history entry.
      const maxPerBatch = config.targetStepId ? Math.floor(BATCH_MAX / 2) : BATCH_MAX;

      while (pendingCards.length > 0) {
        if (config.isCancelled?.()) {
          throw new Error('BREAKOUT_CANCELLED');
        }

        const batchCards = pendingCards.splice(0, Math.max(1, maxPerBatch));
        const batch = firestoreDb.batch();

        for (const breakoutCard of batchCards) {
          const cardRef = this.currentSession.cardListRef.doc(breakoutCard.cardId);
          batch.set(cardRef, breakoutCard.data);

          if (config.targetStepId) {
            const stepHistoryRef = db.cardStepHistory(this.currentSession.id, breakoutCard.cardId);
            const stepHistoryEntry: StepHistoryEvent = {
              stepId: config.targetStepId,
              enteredAt: FieldValue.serverTimestamp() as unknown as Date,
              userId: user.uid,
              userEmail: user.email,
            };
            batch.set(stepHistoryRef.doc(), stepHistoryEntry);
          }
        }

        // eslint-disable-next-line no-await-in-loop
        await batch.commit();

        // Narrow cancel race: user cancelled after commit completed but before we emit progress /
        // run the final transaction (relationship metadata + session cardIds). Without this throw we could
        // commit every batch yet skip updating breakoutChildCardIds — orphans get cleaned up in catch()
        // but undo restoration metadata would never attach to the source card.
        if (config.isCancelled?.()) {
          throw new Error('BREAKOUT_CANCELLED');
        }

        batchCards.forEach((breakoutCard) => {
          emitCardEvent(
            this.currentSession.id,
            breakoutCard.cardId,
            'card.created',
            user.uid,
            user.email,
            {
              via: 'breakout',
              sourceCardId: breakoutCard.data.breakoutParentCardId,
              moduleId: config.moduleId,
            },
          );
        });

        createdCardIds = createdCardIds.concat(batchCards.map((item) => item.cardId));
        config.onProgress?.(createdCardIds.length, totalCardsToCreate);

        if (config.isCancelled?.()) {
          throw new Error('BREAKOUT_CANCELLED');
        }
      }

      // Final cancellation checkpoint immediately before session + source-card metadata transaction.
      if (config.isCancelled?.()) {
        throw new Error('BREAKOUT_CANCELLED');
      }

      await firestoreDb.runTransaction(async (transaction) => {
        transaction.set(
          this.currentSession.sessionRef,
          {
            cardIds: finalCardIds,
          },
          { merge: true },
        );

        for (const sourceCardId of sourceCardIds) {
          const sourceCard = this.rootStore.cardsStore.cards[sourceCardId];
          if (!sourceCard) continue;

          const existingChildIds = sourceCard.data.breakoutChildCardIds ?? [];
          const mergedChildIds = Array.from(
            new Set([...(existingChildIds ?? []), ...(newCardIdsByParent[sourceCardId] ?? [])]),
          );

          transaction.update(this.currentSession.cardListRef.doc(sourceCardId), {
            breakoutChildCardIds: mergedChildIds,
            breakoutModuleId: config.moduleId,
            ...(!config.keepOriginals
              ? {
                  deletedAt: FieldValue.serverTimestamp() as unknown as Date,
                  deletedBy: {
                    userId: user.uid,
                    email: user.email ?? null,
                  },
                }
              : {}),
          });
        }
      });

      createdCardIds.forEach((cardId) => {
        this.rootStore.uiActiveSessionStore.addRecentlyCreatedCard(cardId);
      });

      const undo = async () => {
        try {
          await this.restoreBreakoutState({
            originalCardIds,
            sourceCardSnapshots,
          });
          await this.softDeleteCardDocuments(createdCardIds);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Breakout undo failed:', e);
          this.rootStore.uiSnackbarStore.setSnackbarDetails({
            description: 'Unable to undo breakout. Please refresh the page.',
          });
        }
      };

      const currentStepTabId = this.rootStore.uiActiveSessionStore.currentStepTabId;
      const currentStepId = currentStepTabId === STEP_TAB_TRIAGE ? null : currentStepTabId;
      const isDifferentStep = currentStepId !== config.targetStepId;

      if (isDifferentStep && config.targetStepId) {
        const stepName = this.rootStore.uiActiveSessionStore.getStepNameById(config.targetStepId);
        this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
          undo,
          `${createdCardIds.length} cards created from breakout`,
          () => this.rootStore.uiActiveSessionStore.navigateToStep(config.targetStepId as string),
          `Go to ${stepName}`,
        );
      } else if (isDifferentStep && config.targetStepId === null) {
        this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
          undo,
          `${createdCardIds.length} cards created from breakout`,
          () => this.rootStore.uiActiveSessionStore.navigateToStep(STEP_TAB_TRIAGE),
          'Go to Pending',
        );
      } else {
        this.rootStore.uiSnackbarStore.setUndoDetails(
          undo,
          `${createdCardIds.length} cards created from breakout`,
        );
      }

      return { createdCardIds, undo };
    } catch (error) {
      if (createdCardIds.length > 0) {
        await this.softDeleteCardDocuments(createdCardIds);
      }

      if (error instanceof Error && error.message === 'BREAKOUT_CANCELLED') {
        throw error;
      }

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Unable to break out cards. Please try again.',
      });
      throw error;
    }
  };

  completeOrMoveSelectedCards = async () => {
    if (!this.rootStore.sessionsStore.currentSession) return;

    const { selectedCardIds, currentQueueTab, deselectAllCards } =
      this.rootStore.uiActiveSessionStore;
    const cardIds = [...selectedCardIds];
    const isMovingToDone = currentQueueTab === QueueTabName.InProgress;
    const undo = await (isMovingToDone
      ? this.completeCardOnly(cardIds)
      : this.markCardInProgressOnly(cardIds));
    deselectAllCards();

    const visitAssetFn = this.createVisitAssetFunction(cardIds);
    this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
      undo,
      `${cardIds.length} cards updated`,
      visitAssetFn,
      'Visit Card',
    );
  };

  restoreCard = async (cardId: string) => {
    return this.restoreDeletedCardsToDestination([cardId], RESTORE_DESTINATION_UNASSIGNED);
  };

  restoreCardToDestination = async (cardId: string, destination: RestoreDestination) => {
    return this.restoreDeletedCardsToDestination([cardId], destination);
  };

  restoreSelectedCards = async (
    destination: RestoreDestination = RESTORE_DESTINATION_UNASSIGNED,
    cardIdsOverride?: string[],
  ) => {
    const cardIds = cardIdsOverride
      ? [...cardIdsOverride]
      : [...this.rootStore.uiActiveSessionStore.selectedCardIds];

    if (cardIds.length === 0) return;

    return this.restoreDeletedCardsToDestination(cardIds, destination);
  };

  private async restoreDeletedCardsToDestination(
    cardIds: string[],
    destination: RestoreDestination,
  ) {
    const trashOrder = new Map(
      this.rootStore.deletedCardsStore.deletedCardsList.map((card, index) => [card.id, index]),
    );
    const cardsToRestore = cardIds
      .map((cardId) => this.rootStore.deletedCardsStore.deletedCards[cardId])
      .filter((card): card is Card => !!card)
      .sort(
        (a, b) =>
          (trashOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
          (trashOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER),
      );

    if (cardsToRestore.length === 0) return;

    const originalCardIds = [...this.currentSession.data.cardIds];
    const currentUserId = this.rootStore.authStore.user?.uid ?? '';
    const currentUserEmail = this.rootStore.authStore.user?.email ?? null;
    const destinationLabel =
      getRestoreDestinationDisplayLabel(destination) === destination
        ? this.currentSession.sortedSteps.find((step) => step.id === destination)?.name || 'step'
        : getRestoreDestinationDisplayLabel(destination);
    const originalCardSnapshots = cardsToRestore.reduce(
      (acc, card) => ({
        ...acc,
        [card.id]: {
          deletedAt: card.data.deletedAt,
          deletedBy: card.data.deletedBy,
          doneAt: card.data.doneAt,
          stepId: card.data.stepId ?? null,
          stepEnteredAt: card.data.stepEnteredAt,
        },
      }),
      {} as Record<
        string,
        {
          deletedAt?: Date;
          deletedBy?: { email: string | null; userId: string };
          doneAt?: Date;
          stepId: string | null;
          stepEnteredAt?: Date;
        }
      >,
    );

    try {
      await firestoreDb.runTransaction(async (transaction) => {
        const restoredCardIds = cardsToRestore.map((card) => card.id);
        const restoredCardIdSet = new Set(restoredCardIds);
        const currentCardIdsWithoutRestoredCards = this.currentSession.data.cardIds.filter(
          (cardId) => !restoredCardIdSet.has(cardId),
        );
        const targetCardIds =
          destination === RESTORE_DESTINATION_ARCHIVE
            ? this.currentSession.doneCardIdsByStep
                .slice()
                .reverse()
                .filter((cardId) => !restoredCardIdSet.has(cardId))
            : this.currentSession
                .getCardIdsByStepId(
                  destination === RESTORE_DESTINATION_UNASSIGNED ? null : destination,
                )
                .filter((cardId) => !restoredCardIdSet.has(cardId));
        const nextCardIds = insertCardIdsAtBoundary({
          currentCardIds: currentCardIdsWithoutRestoredCards,
          insertedCardIds: restoredCardIds,
          targetCardIds,
          insertAt: destination === RESTORE_DESTINATION_ARCHIVE ? 'after-last' : 'before-first',
          reverseInsertedCardIds: destination === RESTORE_DESTINATION_ARCHIVE,
        });

        cardsToRestore.forEach((card) => {
          const mutation = getRestoreCardMutation(destination, {
            stepId: card.data.stepId ?? null,
          });
          const cardRef = this.currentSession.cardListRef.doc(card.id);
          const updateData: Record<string, unknown> = {
            deletedAt: FieldValue.delete(),
            deletedBy: FieldValue.delete(),
            stepId: mutation.stepId,
          };

          if (mutation.shouldSetDoneAt) {
            updateData.doneAt = FieldValue.serverTimestamp() as unknown as Date;
          } else if (mutation.shouldClearDoneAt) {
            updateData.doneAt = FieldValue.delete();
          }

          if (mutation.shouldUpdateStepEnteredAt) {
            updateData.stepEnteredAt = FieldValue.serverTimestamp() as unknown as Date;
          }

          transaction.set(cardRef, updateData, { merge: true });
        });

        transaction.set(
          this.currentSession.sessionRef,
          {
            cardIds: nextCardIds,
          },
          { merge: true },
        );
      });

      cardsToRestore.forEach((card) => {
        emitCardEvent(
          this.currentSession.id,
          card.id,
          'card.restored',
          currentUserId,
          currentUserEmail,
        );
      });

      const undo = async () => {
        await firestoreDb.runTransaction(async (transaction) => {
          cardsToRestore.forEach((card) => {
            const snapshot = originalCardSnapshots[card.id];
            const cardRef = this.currentSession.cardListRef.doc(card.id);
            const undoUpdate: Record<string, unknown> = {
              deletedAt: FieldValue.serverTimestamp() as unknown as Date,
              deletedBy: {
                userId: currentUserId,
                email: currentUserEmail,
              } as { email: string | null; userId: string },
              doneAt: snapshot.doneAt ?? FieldValue.delete(),
              stepId: snapshot.stepId,
              stepEnteredAt: snapshot.stepEnteredAt ?? FieldValue.delete(),
            };
            transaction.set(cardRef, undoUpdate, { merge: true });
          });

          transaction.set(
            this.currentSession.sessionRef,
            {
              cardIds: originalCardIds,
            },
            { merge: true },
          );
        });
      };

      this.rootStore.uiActiveSessionStore.deselectCards(cardsToRestore.map((card) => card.id));

      const visitAssetFn = this.createVisitAssetFunction(cardsToRestore.map((card) => card.id));
      this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
        undo,
        cardsToRestore.length === 1
          ? `Card #${cardsToRestore[0].cardNumber} restored to ${destinationLabel}`
          : `${cardsToRestore.length} cards restored to ${destinationLabel}`,
        visitAssetFn,
        'Visit Card',
      );

      return undo;
    } catch (error) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description:
          cardsToRestore.length === 1
            ? `Failed to restore card #${cardsToRestore[0].cardNumber}`
            : 'Failed to restore selected cards',
      });
    }
  }

  addSingleCard = async (cardId: string, position: 'above' | 'below') => {
    if (!this.rootStore.authStore.user) {
      return;
    }

    const targetCard = this.getCardById(cardId);
    if (!targetCard) return;

    const cardIdsCopy = [...this.currentSession.data.cardIds];
    const cardRef = this.currentSession.cardListRef.doc();
    const newCardId = cardRef.id;

    const uid = this.rootStore.authStore.user?.uid ?? '';
    const userEmail = this.rootStore.authStore.user?.email ?? null;

    // Create the card in Firestore
    await cardRef.set({
      createdAt: FieldValue.serverTimestamp() as unknown as Date,
      createdBy: { userId: uid, email: userEmail },
      connections: {},
    });

    emitCardEvent(this.currentSession.id, newCardId, 'card.created', uid, userEmail, {
      via: 'manual',
    });

    // Insert the card in the correct position
    const targetIdx = cardIdsCopy.indexOf(cardId);
    if (targetIdx === -1) {
      // Card not found, fallback to adding at the end
      cardIdsCopy.push(newCardId);
    } else if (position === 'above') {
      cardIdsCopy.splice(targetIdx, 0, newCardId);
    } else {
      cardIdsCopy.splice(targetIdx + 1, 0, newCardId);
    }

    await this.currentSession.sessionRef.set(
      {
        cardIds: cardIdsCopy,
      },
      { merge: true },
    );

    // Add glow animation for the newly created card
    this.rootStore.uiActiveSessionStore.addRecentlyCreatedCard(newCardId);

    const positionText = position === 'above' ? 'above' : 'below';
    this.rootStore.uiSnackbarStore.setSnackbarDetails({
      description: `Card added ${positionText} Card #${targetCard.cardNumber}`,
    });
  };

  /**
   * Create a single blank card appended at the end of the session, optionally
   * already placed in a step. Returns the new card id (so the caller can focus
   * it). Mirrors `addSingleCard` but needs no reference card — used by the Whole
   * View "+ look" affordance to author a look directly into a step group.
   * (`batchAddCards([])` creates nothing: its loop is driven by the input array.)
   */
  addBlankCard = async (stepId: string | null = null): Promise<string | undefined> => {
    if (!this.rootStore.authStore.user) {
      return undefined;
    }

    const cardRef = this.currentSession.cardListRef.doc();
    const newCardId = cardRef.id;
    const uid = this.rootStore.authStore.user?.uid ?? '';
    const userEmail = this.rootStore.authStore.user?.email ?? null;

    await cardRef.set({
      createdAt: FieldValue.serverTimestamp() as unknown as Date,
      createdBy: { userId: uid, email: userEmail },
      connections: {},
      ...(stepId ? { stepId, stepEnteredAt: FieldValue.serverTimestamp() as unknown as Date } : {}),
    });

    emitCardEvent(this.currentSession.id, newCardId, 'card.created', uid, userEmail, {
      via: 'manual',
    });

    await this.currentSession.sessionRef.set(
      { cardIds: FieldValue.arrayUnion(newCardId) as unknown as string[] },
      { merge: true },
    );

    this.rootStore.uiActiveSessionStore.addRecentlyCreatedCard(newCardId);
    return newCardId;
  };

  async batchAddCards(
    targetCards: Card[],
    afterCardId?: string,
    addPosition: 'top' | 'bottom' = 'bottom',
    stepId: string | null = null,
    markAsDone = false,
  ) {
    if (!this.rootStore.authStore.user) {
      return;
    }

    const targetCardsCopy = [...targetCards];

    const cardIdsCopy = [...this.currentSession.data.cardIds];
    const newCardIds: string[] = [];
    const batchUid = this.rootStore.authStore.user?.uid ?? '';
    const batchEmail = this.rootStore.authStore.user?.email ?? null;
    while (targetCardsCopy.length > 0) {
      const batchCards = targetCardsCopy.splice(0, BATCH_MAX / 3);
      const batchNewIds: string[] = [];
      // eslint-disable-next-line no-await-in-loop
      await firestoreDb.runTransaction(async (transaction) => {
        for (const targetCard of batchCards) {
          const cardRef = this.currentSession.cardListRef.doc();
          // eslint-disable-next-line no-await-in-loop
          batchNewIds.push(cardRef.id);
          newCardIds.push(cardRef.id);
          transaction.set(cardRef, {
            createdAt: FieldValue.serverTimestamp() as unknown as Date,
            createdBy: { userId: batchUid, email: batchEmail },
            connections: targetCard.data.connections,
            ...(targetCard.data.suffix ? { suffix: targetCard.data.suffix } : {}),
            ...(stepId
              ? { stepId, stepEnteredAt: FieldValue.serverTimestamp() as unknown as Date }
              : {}),
            ...(markAsDone ? { doneAt: FieldValue.serverTimestamp() as unknown as Date } : {}),
          });
        }
      });

      batchNewIds.forEach((id) => {
        emitCardEvent(this.currentSession.id, id, 'card.created', batchUid, batchEmail, {
          via: 'batch',
        });
      });
    }

    if (afterCardId) {
      // Insert relative to the specified card
      const insertIndex = cardIdsCopy.indexOf(afterCardId);
      if (insertIndex === -1) {
        // Card not found, fallback to absolute positioning
        if (addPosition === 'top') {
          cardIdsCopy.splice(0, 0, ...newCardIds);
        } else {
          cardIdsCopy.push(...newCardIds);
        }
      } else if (addPosition === 'top') {
        // Insert above (at the card's position)
        cardIdsCopy.splice(insertIndex, 0, ...newCardIds);
      } else {
        // Insert below (after the card's position)
        cardIdsCopy.splice(insertIndex + 1, 0, ...newCardIds);
      }
    } else {
      // No specific card reference, use absolute positioning
      if (addPosition === 'top') {
        cardIdsCopy.splice(0, 0, ...newCardIds);
      } else {
        cardIdsCopy.push(...newCardIds);
      }
    }

    // Clear any existing selections after batch add
    this.rootStore.uiActiveSessionStore.deselectAllCards();

    await this.currentSession.sessionRef.set(
      {
        cardIds: cardIdsCopy,
      },
      { merge: true },
    );

    // Add glow animation for newly created cards
    setTimeout(() => {
      runInAction(() => {
        newCardIds.forEach((cardId) => {
          this.rootStore.uiActiveSessionStore.addRecentlyCreatedCard(cardId);
        });
      });
    }, 100);

    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      this.rootStore.uiActiveSessionStore.deselectAllCards();
      await this.batchRemoveCards(newCardIds);
    }, `${newCardIds.length} Cards Added to Pending (Selected)`);
  }

  private sanitizeDateEntry(entry: CardDateEntry): CardDateEntry {
    // Create sanitized copy - for discriminated union, customLabel is only present for custom type
    const sanitized = { ...entry } as CardDateEntry;

    // Remove optional fields if empty (but preserve customLabel for custom type)
    if (!sanitized.startDate) delete sanitized.startDate;
    if (!sanitized.endDate) delete sanitized.endDate;
    if (!sanitized.tentativeNote) delete sanitized.tentativeNote;

    // Note: customLabel is required for 'custom' type and should not exist for other types
    // The discriminated union type ensures this at compile time

    return sanitized;
  }

  private sanitizeDateEntries(entries: CardDateEntry[]) {
    return entries.map((entry) => this.sanitizeDateEntry(entry));
  }

  private updateDatesForCard(
    cardId: string,
    updater: (entries: CardDateEntry[]) => CardDateEntry[],
  ) {
    const card = this.getCardById(cardId);
    const existing = card?.data.dates ?? [];
    return this.sanitizeDateEntries(updater(existing));
  }

  addOrUpdateCardDateEntry = async (cardId: string, entry: CardDateEntry) => {
    const existingDates = this.getCardById(cardId)?.data.dates ?? [];
    const existingEntry = existingDates.find((e) =>
      entry.type === 'custom' ? e.id === entry.id : e.type === entry.type,
    );
    const updatedDates = this.updateDatesForCard(cardId, (entries) => {
      const index = entries.findIndex((e) =>
        entry.type === 'custom' ? e.id === entry.id : e.type === entry.type,
      );
      if (index >= 0) {
        const next = [...entries];
        next[index] = entry;
        return next;
      }
      return [...entries, entry];
    });

    await this.currentSession.cardListRef.doc(cardId).update({
      dates: updatedDates,
    });

    emitCardEvent(
      this.currentSession.id,
      cardId,
      'dates.changed',
      this.rootStore.authStore.user?.uid ?? '',
      this.rootStore.authStore.user?.email ?? null,
      buildDatesChangedMeta({ action: existingEntry ? 'updated' : 'added', entry }),
    );
  };

  batchAddOrUpdateCardDateEntry = async (cardIds: string[], entry: CardDateEntry) => {
    const cardIdsCopy = [...cardIds];
    const userId = this.rootStore.authStore.user?.uid ?? '';
    const userEmail = this.rootStore.authStore.user?.email ?? null;

    while (cardIdsCopy.length > 0) {
      const batchCardIds = cardIdsCopy.splice(0, BATCH_MAX);
      const pendingEvents = batchCardIds.map((cardId) => {
        const existingDates = this.getCardById(cardId)?.data.dates ?? [];
        const existingEntry = existingDates.find((e) =>
          entry.type === 'custom' ? e.id === entry.id : e.type === entry.type,
        );
        return {
          cardId,
          type: 'dates.changed' as const,
          userId,
          userEmail,
          meta: buildDatesChangedMeta({ action: existingEntry ? 'updated' : 'added', entry }),
        };
      });
      // eslint-disable-next-line no-await-in-loop
      await safeBatch(firestoreDb, batchCardIds, (batch, cardId) => {
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        const updatedDates = this.updateDatesForCard(cardId, (entries) => {
          const index = entries.findIndex((e) =>
            entry.type === 'custom' ? e.id === entry.id : e.type === entry.type,
          );
          if (index >= 0) {
            const next = [...entries];
            next[index] = entry;
            return next;
          }
          return [...entries, entry];
        });

        batch.update(cardRef, { dates: updatedDates });
      });

      pendingEvents.forEach(
        ({ cardId, type, meta, userId: eventUserId, userEmail: eventUserEmail }) => {
          emitCardEvent(this.currentSession.id, cardId, type, eventUserId, eventUserEmail, meta);
        },
      );
    }
  };

  updateCardDateTentativeStatus = async (
    cardIds: string[],
    type: CardDateType,
    isTentative: boolean,
  ) => {
    const cardIdsCopy = [...cardIds];
    const userId = this.rootStore.authStore.user?.uid ?? '';
    const userEmail = this.rootStore.authStore.user?.email ?? null;

    while (cardIdsCopy.length > 0) {
      const batchCardIds = cardIdsCopy.splice(0, BATCH_MAX);
      const pendingEvents = batchCardIds
        .map((cardId) => {
          const existingDates = this.getCardById(cardId)?.data.dates ?? [];
          const existingEntry = existingDates.find((entryItem) => entryItem.type === type);
          return existingEntry
            ? {
                cardId,
                type: 'dates.changed' as const,
                userId,
                userEmail,
                meta: buildDatesChangedMeta({
                  action: 'updated',
                  entry: { ...existingEntry, isTentative },
                }),
              }
            : null;
        })
        .filter((event): event is NonNullable<typeof event> => event !== null);
      // eslint-disable-next-line no-await-in-loop
      await safeBatch(firestoreDb, batchCardIds, (batch, cardId) => {
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        const updatedDates = this.updateDatesForCard(cardId, (entries) =>
          entries.map((entryItem) =>
            entryItem.type === type ? { ...entryItem, isTentative } : entryItem,
          ),
        );

        batch.update(cardRef, { dates: updatedDates });
      });

      pendingEvents.forEach(
        ({ cardId, type: eventType, meta, userId: eventUserId, userEmail: eventUserEmail }) => {
          emitCardEvent(
            this.currentSession.id,
            cardId,
            eventType,
            eventUserId,
            eventUserEmail,
            meta,
          );
        },
      );
    }
  };

  clearCardDateType = async (cardIds: string[], type: CardDateType) => {
    const cardIdsCopy = [...cardIds];
    const userId = this.rootStore.authStore.user?.uid ?? '';
    const userEmail = this.rootStore.authStore.user?.email ?? null;

    while (cardIdsCopy.length > 0) {
      const batchCardIds = cardIdsCopy.splice(0, BATCH_MAX);
      const pendingEvents = batchCardIds
        .map((cardId) => {
          const existingDates = this.getCardById(cardId)?.data.dates ?? [];
          const existingEntry = existingDates.find((entryItem) => entryItem.type === type);
          return existingEntry
            ? {
                cardId,
                type: 'dates.changed' as const,
                userId,
                userEmail,
                meta: buildDatesChangedMeta({ action: 'removed', entry: existingEntry }),
              }
            : null;
        })
        .filter((event): event is NonNullable<typeof event> => event !== null);
      // eslint-disable-next-line no-await-in-loop
      await safeBatch(firestoreDb, batchCardIds, (batch, cardId) => {
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        const updatedDates = this.updateDatesForCard(cardId, (entries) =>
          entries.filter((entryItem) => entryItem.type !== type),
        );

        batch.update(cardRef, { dates: updatedDates });
      });

      pendingEvents.forEach(
        ({ cardId, type: eventType, meta, userId: eventUserId, userEmail: eventUserEmail }) => {
          emitCardEvent(
            this.currentSession.id,
            cardId,
            eventType,
            eventUserId,
            eventUserEmail,
            meta,
          );
        },
      );
    }
  };

  removeCardDateEntry = async (cardId: string, entryType: CardDateType) => {
    const updatedDates = this.updateDatesForCard(cardId, (entries) =>
      entries.filter((entryItem) => entryItem.type !== entryType),
    );
    const removedEntry = (this.getCardById(cardId)?.data.dates ?? []).find(
      (entryItem) => entryItem.type === entryType,
    );

    await this.currentSession.cardListRef.doc(cardId).update({
      dates: updatedDates,
    });

    emitCardEvent(
      this.currentSession.id,
      cardId,
      'dates.changed',
      this.rootStore.authStore.user?.uid ?? '',
      this.rootStore.authStore.user?.email ?? null,
      removedEntry
        ? buildDatesChangedMeta({ action: 'removed', entry: removedEntry })
        : { dateType: entryType, action: 'removed' },
    );
  };

  /**
   * Set all dates for a single card (replaces existing dates)
   */
  setCardDates = async (cardId: string, entries: CardDateEntry[]) => {
    const sanitizedDates = this.sanitizeDateEntries(entries);
    const previousDates = this.getCardById(cardId)?.data.dates ?? [];
    const pendingEvents = buildDateChangeEventsForCard({
      cardId,
      previousEntries: previousDates,
      nextEntries: sanitizedDates,
      userId: this.rootStore.authStore.user?.uid ?? '',
      userEmail: this.rootStore.authStore.user?.email ?? null,
    });

    await this.currentSession.cardListRef.doc(cardId).update({
      dates: sanitizedDates.length > 0 ? sanitizedDates : FieldValue.delete(),
    });

    pendingEvents.forEach(({ cardId: eventCardId, type, meta, userId, userEmail }) => {
      emitCardEvent(this.currentSession.id, eventCardId, type, userId, userEmail, meta);
    });
  };

  /**
   * Set dates for multiple cards (replaces existing dates on each card)
   */
  batchSetCardDates = async (cardIds: string[], entries: CardDateEntry[]) => {
    const sanitizedDates = this.sanitizeDateEntries(entries);
    const cardIdsCopy = [...cardIds];
    const userId = this.rootStore.authStore.user?.uid ?? '';
    const userEmail = this.rootStore.authStore.user?.email ?? null;

    while (cardIdsCopy.length > 0) {
      const batchCardIds = cardIdsCopy.splice(0, BATCH_MAX);
      const pendingEvents = batchCardIds.flatMap((cardId) =>
        buildDateChangeEventsForCard({
          cardId,
          previousEntries: this.getCardById(cardId)?.data.dates ?? [],
          nextEntries: sanitizedDates,
          userId,
          userEmail,
        }),
      );
      // eslint-disable-next-line no-await-in-loop
      await safeBatch(firestoreDb, batchCardIds, (batch, cardId) => {
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        batch.update(cardRef, {
          dates: sanitizedDates.length > 0 ? sanitizedDates : FieldValue.delete(),
        });
      });

      pendingEvents.forEach(
        ({ cardId, type, meta, userId: eventUserId, userEmail: eventUserEmail }) => {
          emitCardEvent(this.currentSession.id, cardId, type, eventUserId, eventUserEmail, meta);
        },
      );
    }
  };

  // ===== Priority Actions =====

  /**
   * Set priority for a single card
   */
  setCardPriority = async (cardId: string, priority: CardPriority | null) => {
    const card = this.getCardById(cardId);
    if (!card) return;

    const previousPriority = card.data.priority;

    await this.currentSession.cardListRef.doc(cardId).update({
      priority: priority ?? FieldValue.delete(),
    });

    emitCardEvent(
      this.currentSession.id,
      cardId,
      'priority.changed',
      this.rootStore.authStore.user?.uid ?? '',
      this.rootStore.authStore.user?.email ?? null,
      { from: previousPriority ?? null, to: priority ?? null },
    );

    const priorityLabel = priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'None';
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      await this.currentSession.cardListRef.doc(cardId).update({
        priority: previousPriority ?? FieldValue.delete(),
      });
    }, `Card #${card.cardNumber} priority set to ${priorityLabel}`);
  };

  /**
   * Set priority for multiple cards (batch operation)
   */
  setSelectedCardsPriority = async (priority: CardPriority | null) => {
    const { selectedCardIds } = this.rootStore.uiActiveSessionStore;
    const cardIds = [...selectedCardIds];

    if (cardIds.length === 0) return;

    // Store previous priorities for undo
    const previousPriorities: Record<string, CardPriority | null | undefined> = {};
    cardIds.forEach((cardId) => {
      const card = this.getCardById(cardId);
      if (card) {
        previousPriorities[cardId] = card.data.priority;
      }
    });

    const priorityUid = this.rootStore.authStore.user?.uid ?? '';
    const priorityEmail = this.rootStore.authStore.user?.email ?? null;
    const cardIdsCopy = [...cardIds];
    while (cardIdsCopy.length > 0) {
      const batchCardIds = cardIdsCopy.splice(0, BATCH_MAX);
      // eslint-disable-next-line no-await-in-loop
      await safeBatch(firestoreDb, batchCardIds, (batch, cardId) => {
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        batch.update(cardRef, {
          priority: priority ?? FieldValue.delete(),
        });
      });
    }

    cardIds.forEach((cardId) => {
      emitCardEvent(
        this.currentSession.id,
        cardId,
        'priority.changed',
        priorityUid,
        priorityEmail,
        {
          from: previousPriorities[cardId] ?? null,
          to: priority ?? null,
        },
      );
    });

    const priorityLabel = priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'None';
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const restoreIds = Object.keys(previousPriorities);
      while (restoreIds.length > 0) {
        const batchIds = restoreIds.splice(0, BATCH_MAX);
        // eslint-disable-next-line no-await-in-loop
        await safeBatch(firestoreDb, batchIds, (batch, cardId) => {
          const cardRef = this.currentSession.cardListRef.doc(cardId);
          const prevPriority = previousPriorities[cardId];
          batch.update(cardRef, {
            priority: prevPriority ?? FieldValue.delete(),
          });
        });
      }
    }, `${cardIds.length} cards priority set to ${priorityLabel}`);
  };

  /**
   * Set priority for specific card IDs (for dialog use)
   */
  batchSetCardPriority = async (cardIds: string[], priority: CardPriority | null) => {
    if (cardIds.length === 0) return;

    const bpUid = this.rootStore.authStore.user?.uid ?? '';
    const bpEmail = this.rootStore.authStore.user?.email ?? null;
    const cardIdsCopy = [...cardIds];
    while (cardIdsCopy.length > 0) {
      const batchCardIds = cardIdsCopy.splice(0, BATCH_MAX);
      // eslint-disable-next-line no-await-in-loop
      await safeBatch(firestoreDb, batchCardIds, (batch, cardId) => {
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        batch.update(cardRef, {
          priority: priority ?? FieldValue.delete(),
        });
      });
    }

    cardIds.forEach((cardId) => {
      emitCardEvent(this.currentSession.id, cardId, 'priority.changed', bpUid, bpEmail, {
        to: priority ?? null,
      });
    });
  };

  // ===== Issue Actions =====

  /**
   * Report an issue for one or more cards
   */
  reportCardIssue = async (cardIds: string[], message: string) => {
    if (cardIds.length === 0 || !message.trim()) return;

    const issue: CardIssue = {
      status: 'open',
      message: message.trim(),
      reportedAt: new Date(),
      reportedBy: {
        userId: this.rootStore.authStore.user?.uid ?? '',
        email: this.rootStore.authStore.user?.email ?? null,
      },
    };

    const issueUid = this.rootStore.authStore.user?.uid ?? '';
    const issueEmail = this.rootStore.authStore.user?.email ?? null;
    const cardIdsCopy = [...cardIds];
    while (cardIdsCopy.length > 0) {
      const batchCardIds = cardIdsCopy.splice(0, BATCH_MAX);
      // eslint-disable-next-line no-await-in-loop
      await safeBatch(firestoreDb, batchCardIds, (batch, cardId) => {
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        batch.update(cardRef, { issue });
      });
    }

    cardIds.forEach((cardId) => {
      emitCardEvent(this.currentSession.id, cardId, 'issue.reported', issueUid, issueEmail, {
        message: issue.message,
      });
    });
  };

  /**
   * Resolve issues for one or more cards
   */
  resolveCardIssue = async (cardIds: string[]) => {
    if (cardIds.length === 0) return;

    const resolveUid = this.rootStore.authStore.user?.uid ?? '';
    const resolveEmail = this.rootStore.authStore.user?.email ?? null;
    const issueMessages: Record<string, string> = {};
    cardIds.forEach((cardId) => {
      const card = this.getCardById(cardId);
      if (card?.data.issue) {
        issueMessages[cardId] = card.data.issue.message;
      }
    });

    const cardIdsCopy = [...cardIds];
    while (cardIdsCopy.length > 0) {
      const batchCardIds = cardIdsCopy.splice(0, BATCH_MAX);
      // eslint-disable-next-line no-await-in-loop
      await safeBatch(firestoreDb, batchCardIds, (batch, cardId) => {
        if (!issueMessages[cardId]) return;
        const cardRef = this.currentSession.cardListRef.doc(cardId);
        batch.update(cardRef, {
          issue: FieldValue.delete(),
        });
      });
    }

    cardIds.forEach((cardId) => {
      if (issueMessages[cardId]) {
        emitCardEvent(this.currentSession.id, cardId, 'issue.resolved', resolveUid, resolveEmail, {
          message: issueMessages[cardId],
        });
      }
    });
  };
}
